import { Formik, FormikProps } from "formik";
import React, { FunctionComponent, memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { IAttachment, TAttachmentPurpose } from "@/data/attachments";
import { Flex } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { ImageSection } from "@/views/components/Files";
import { Messenger } from "@/views/components/Messenger";

import { ICompanyFormikProps } from "../types";

export const validationCompanyLogoSectionSchema = Yup.object();

export interface ICompanyLogoFormValues {
  logo: IAttachment | null;
}

export const COMPANY_LOGO_MESSENGER_CONTEXT = "company-logo-form";

interface ICompanyLogoFormProps {
  form: FormikProps<ICompanyLogoFormValues>;
  onCancel: () => void;
}

export const CompanyLogoForm: FunctionComponent<ICompanyLogoFormProps> = ({ onCancel, form }) => {
  const { t } = useTranslation("session");

  const renderPlaceholder = () => (
    <Section
      width={12}
      background="background-card"
      padding={["xl"]}
      shadow="none"
      borderRadius
      flex
      justify="center"
      align="center">
      <Icon icon="company" color="grey3" size="xxl" />
    </Section>
  );

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="md">
        <Messenger context={COMPANY_LOGO_MESSENGER_CONTEXT} />

        <ImageSection
          attachmentType={TAttachmentPurpose.LOGO}
          value={form.values.logo}
          name="logo"
          testName="logo"
          renderPlaceholder={renderPlaceholder}
        />

        <Flex innerMargin="md" justify="flex-end">
          <Button variant="secondary" size="lg" testName="cancel" onClick={onCancel}>
            {t("COMPANY.MY_COMPANY.DATA.LOGO_EDIT_DIALOG.BUTTON_CANCEL")}
          </Button>
          <Button size="lg" testName="save" onClick={form.submitForm} disabled={form.isSubmitting}>
            {t("COMPANY.MY_COMPANY.DATA.LOGO_EDIT_DIALOG.BUTTON_SAVE")}
          </Button>
        </Flex>
      </Flex>
    </FormikForm>
  );
};

interface ICompanyLogoFormikProps extends ICompanyFormikProps<ICompanyLogoFormValues>, ICompanyLogoFormProps {}

export const CompanyLogoFormik: FunctionComponent<ICompanyLogoFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<ICompanyLogoFormValues>) => <CompanyLogoForm {...rest} form={form} />}
    />
  ),
);
